
import { format, addDays, isWeekend, differenceInBusinessDays, isSaturday, isSunday, parse } from "date-fns";

// Helper function to safely parse date strings and avoid timezone issues
const parseLocalDate = (dateString: string): Date => {
  // If the date string is in DD MMM YYYY format (like from our forms)
  if (dateString.match(/^\d{2} [A-Za-z]{3} \d{4}$/)) {
    return parse(dateString, 'dd MMM yyyy', new Date());
  }
  
  // If the date string is in YYYY-MM-DD format
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
  }
  
  // For other formats, try parsing with Date constructor but add noon time to avoid timezone issues
  const date = new Date(dateString + 'T12:00:00');
  if (!isNaN(date.getTime())) {
    return date;
  }
  
  // Fallback to regular Date constructor
  return new Date(dateString);
};

// Function to calculate end date based on start date and total duration
export const calculateEndDate = (startDate: string, durationDays: number, excludeWeekends: boolean = true): string => {
  if (!startDate) return '';
  if (durationDays <= 0) return '';
  
  try {
    let currentDate = parseLocalDate(startDate);
    
    // Ensure valid date
    if (isNaN(currentDate.getTime())) {
      console.error("Invalid start date:", startDate);
      return '';
    }
    
    if (excludeWeekends) {
      // Count the start date itself as day 1, then add the remaining business days
      let remainingBusinessDays = Math.max(durationDays - 1, 0);

      while (remainingBusinessDays > 0) {
        currentDate = addDays(currentDate, 1);
        if (!isWeekend(currentDate)) {
          remainingBusinessDays--;
        }
      }
    } else {
      // Add calendar days (including weekends)
      currentDate = addDays(currentDate, durationDays);
    }
    
    return format(currentDate, 'dd MMM yyyy');
  } catch (error) {
    console.error("Error calculating end date:", error);
    return '';
  }
};

// Function to calculate duration between two dates in business days
export const calculateDuration = (startDate: string, endDate: string, excludeWeekends: boolean = true): number => {
  if (!startDate || !endDate) return 0;
  
  try {
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    
    // Ensure valid dates
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.error("Invalid date inputs:", { startDate, endDate });
      return 0;
    }
    
    if (excludeWeekends) {
      return differenceInBusinessDays(end, start);
    } else {
      // Calculate all calendar days between dates
      const diffTime = Math.abs(end.getTime() - start.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
  } catch (error) {
    console.error("Error calculating duration:", error);
    return 0;
  }
};

// Function to calculate task end date considering weekends option
export const calculateTaskEndDate = (
  startDate: Date, 
  duration: number, 
  excludeWeekends: boolean = true
): Date => {
  try {
    // Ensure valid date
    if (isNaN(startDate.getTime())) {
      console.error("Invalid start date in calculateTaskEndDate:", startDate);
      return new Date();
    }
    
    if (excludeWeekends) {
      // Skip weekends when calculating end date
      // Start counting duration from the start date itself
      let endDate = new Date(startDate);
      let daysToAdd = duration - 1; // Subtract 1 because start date counts as day 1
      
      // If duration is 1, the end date is the same as start date
      if (duration <= 1) {
        return endDate;
      }
      
      while (daysToAdd > 0) {
        endDate = addDays(endDate, 1);
        if (!isWeekend(endDate)) {
          daysToAdd--;
        }
      }
      
      // Ensure the end date is not a weekend
      while (isWeekend(endDate)) {
        endDate = addDays(endDate, 1);
      }
      
      return endDate;
    } else {
      // Include weekends when calculating end date
      return addDays(startDate, duration - 1); // Subtract 1 because start date counts as day 1
    }
  } catch (error) {
    console.error("Error in calculateTaskEndDate:", error);
    return new Date();
  }
};

// Function to check if a date is a weekend
export const isDateWeekend = (date: Date): boolean => {
  try {
    // Ensure valid date
    if (isNaN(date.getTime())) {
      console.error("Invalid date in isDateWeekend:", date);
      return false;
    }
    
    return isSaturday(date) || isSunday(date);
  } catch (error) {
    console.error("Error in isDateWeekend:", error);
    return false;
  }
};

// Function to get the next working day after a given date
export const getNextWorkingDay = (date: Date, excludeWeekends: boolean = true): Date => {
  try {
    // Ensure valid date
    if (isNaN(date.getTime())) {
      console.error("Invalid date in getNextWorkingDay:", date);
      return new Date();
    }
    
    if (!excludeWeekends) {
      return addDays(date, 1);
    }
    
    let nextDay = addDays(date, 1);
    
    // Skip weekends if needed
    while (isDateWeekend(nextDay)) {
      nextDay = addDays(nextDay, 1);
    }
    
    return nextDay;
  } catch (error) {
    console.error("Error in getNextWorkingDay:", error);
    return addDays(date, 1);
  }
};

// Function to calculate all task dates in a sequence
export const calculateSequentialTaskDates = (
  projectStartDate: string,
  tasks: { duration: number }[],
  excludeWeekends: boolean = true
): { startDates: string[], endDates: string[] } => {
  const startDates: string[] = [];
  const endDates: string[] = [];
  
  if (!projectStartDate || !tasks.length) {
    console.log("No project start date or tasks provided");
    return { startDates, endDates };
  }
  
  try {
    let currentTaskStartDate = parseLocalDate(projectStartDate);
    
    // Ensure valid date
    if (isNaN(currentTaskStartDate.getTime())) {
      console.error("Invalid project start date:", projectStartDate);
      return { startDates, endDates };
    }
    
    tasks.forEach((task, index) => {
      console.log(`Processing task ${index}, duration: ${task.duration}`);
      
      // For the first task, start on the project start date
      // For subsequent tasks, start on the next working day after the previous task's end date
      if (index > 0) {
        try {
          const prevEndDate = parseLocalDate(endDates[index - 1]);
          currentTaskStartDate = getNextWorkingDay(prevEndDate, excludeWeekends);
          console.log(`Next task starts on: ${format(currentTaskStartDate, 'dd MMM yyyy')}`);
        } catch (error) {
          console.error(`Error calculating start date for task ${index}:`, error);
          // Use a fallback if there's an error
          currentTaskStartDate = addDays(currentTaskStartDate, 1);
        }
      }
      
      // Calculate end date for this task
      let currentTaskEndDate;
      try {
        currentTaskEndDate = calculateTaskEndDate(
          currentTaskStartDate,
          task.duration,
          excludeWeekends
        );
        console.log(`Task ${index} ends on: ${format(currentTaskEndDate, 'dd MMM yyyy')}`);
      } catch (error) {
        console.error(`Error calculating end date for task ${index}:`, error);
        // Use a fallback if there's an error
        currentTaskEndDate = addDays(currentTaskStartDate, task.duration - 1);
      }
      
      // Store dates in ISO format
      try {
        startDates.push(format(currentTaskStartDate, 'dd MMM yyyy'));
        endDates.push(format(currentTaskEndDate, 'dd MMM yyyy'));
      } catch (error) {
        console.error(`Error formatting dates for task ${index}:`, error);
        // Add empty strings as fallback
        startDates.push('');
        endDates.push('');
      }
    });
    
    return { startDates, endDates };
  } catch (error) {
    console.error("Error in calculateSequentialTaskDates:", error);
    return { startDates, endDates };
  }
};
